import { useCallback, useEffect, useState } from 'react';

// Internal-only setting (gated by the `internal_save_generated_lessons_as_free`
// Unleash flag). When enabled, lessons the user generates have their premium
// markers stripped (`aiContent.isPremiumGenerator = false`,
// `aiContent.generatorPlan = 'freemium'`), so they appear as free content to
// recipients and don't consume teaching-session credits.
//
// Persisted to localStorage following the same pattern as `useLocale`'s
// `curipod.preferredLocale`. Per-device, not synced to the backend — the
// setting only matters at generation time on the device performing the
// generation.

const STORAGE_KEY = 'curipod.saveGeneratedLessonsAsFree';

function readFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === 'true';
}

export function useSaveGeneratedLessonsAsFree() {
  // Initialize from `false` on the server and during the first client render
  // so SSR markup matches; pull the real value in a post-hydration effect.
  const [enabled, setEnabledState] = useState<boolean>(false);

  useEffect(() => {
    setEnabledState(readFromStorage());
  }, []);

  const setEnabled = useCallback((next: boolean) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, String(next));
    }
    setEnabledState(next);
  }, []);

  return { enabled, setEnabled };
}

// Synchronous read for use outside React (e.g. inside a one-shot generation
// callback that doesn't need to subscribe to state changes). The override is
// active only when both the internal Unleash flag and the local toggle are on.
export function isSaveGeneratedLessonsAsFreeEnabled(
  isInternalSaveGeneratedLessonsAsFreeFlagEnabled: boolean,
): boolean {
  if (!isInternalSaveGeneratedLessonsAsFreeFlagEnabled) return false;
  return readFromStorage();
}
